import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
	AccessGroup,
	AccessGroupType,
	AccessControlType,
	I18nType,
	User,
	UserType,
} from '@schemas'
import type {
	AccessControlStateType,
	SecetionUserListType,
	ApiGetDataStatus,
} from '@redux/types'

export const emptyGroup: AccessGroupType = {
	id: '',
	name: '',
	i18n: [],
	users: [],
	createdBy: '',
	createdAt: '',
}

// initial state
const initialState: AccessControlStateType = {
	// trigger
	triggerInitAccessControl: false,
	triggerGetUsers: false,
	triggerGetGroup: false,

	// tab
	selectedTab: 'group',

	// users list
	userListStatus: 'init',
	userList: [],
	userSectionList: [],
	openedUserCateg: [],

	// group list
	groupListStatus: 'init',
	groupList: [],
	addGroup: emptyGroup,
	updateGroup: emptyGroup,
	updateAccessGroupUsers: {
		accessGroupId: '',
		userIds: [],
	},
	deleteGroup: '',
	updateAccessGroupI18n: {
		id: '',
		tableName: '',
		tableId: '',
		menuId: '',
		isoLanguageCode: '',
		title: '',
		value: '',
	},

	// get access control
	userAccessControlStatus: 'init',
	groupAccessControlStatus: 'init',
	selectedAccessUser: new User(),
	selectedAccessGroup: new AccessGroup(),
	selectedAccessGroupByUserTab: new AccessGroup(),
	groupListByUserTab: [],

	// access control
	selectedGroupAccessControl: [],
	selectedGroupAccessControlByUserTab: [],
	openedUserAccessControl: [],

	// access control action
	addAccessControl: {
		menuId: '',
		crud: '',
	},
	updateAccessControl: {
		accessControlId: '',
		menuId: '',
		crud: '',
	},
	deleteAccessControl: '',

	// left modal
	showSideModal: false,
	leftModalInfo: {
		target: 'groupEditor',
	},
	leftModalTabIndex: 0,
}

const slice = createSlice({
	name: 'accessControl',
	initialState,
	reducers: {
		// trigger
		TRIGGER_INIT_ACCESS_CONTROL: (
			state: AccessControlStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInitAccessControl = action.payload
		},
		TRIGGER_GET_USERS: (
			state: AccessControlStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetUsers = action.payload
		},
		TRIGGER_GET_GROUP: (
			state: AccessControlStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetGroup = action.payload
		},

		// tab
		SELECTED_TAB: (state: AccessControlStateType, action: PayloadAction<string>) => {
			state.selectedTab = action.payload
		},

		// user list
		USER_LIST_STATUS: (
			state: AccessControlStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.userListStatus = action.payload
		},
		USER_LIST: (state: AccessControlStateType, action: PayloadAction<UserType[]>) => {
			state.userList = action.payload
		},
		USER_SECTION_LIST: (
			state: AccessControlStateType,
			action: PayloadAction<SecetionUserListType[]>,
		) => {
			state.userSectionList = action.payload
		},
		OPENED_USER_CATEG: (
			state: AccessControlStateType,
			action: PayloadAction<string[]>,
		) => {
			state.openedUserCateg = action.payload
		},

		// group list
		GROUP_LIST_STATUS: (
			state: AccessControlStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.groupListStatus = action.payload
		},
		GROUP_LIST: (
			state: AccessControlStateType,
			action: PayloadAction<AccessGroupType[]>,
		) => {
			state.groupList = action.payload
		},
		ADD_GROUP: (
			state: AccessControlStateType,
			action: PayloadAction<AccessGroupType>,
		) => {
			state.updateGroup = action.payload
		},
		UPDATE_GROUP: (
			state: AccessControlStateType,
			action: PayloadAction<AccessGroupType>,
		) => {
			state.updateGroup = action.payload
		},
		UPDATE_ACCESS_GROUP_USERS: (
			state: AccessControlStateType,
			action: PayloadAction<AccessControlStateType['updateAccessGroupUsers']>,
		) => {
			state.updateAccessGroupUsers = action.payload
		},
		DELETE_GROUP: (state: AccessControlStateType, action: PayloadAction<string>) => {
			state.deleteGroup = action.payload
		},
		UPDATE_ACCESS_GROUP_I18N: (
			state: AccessControlStateType,
			action: PayloadAction<I18nType>,
		) => {
			state.updateAccessGroupI18n = action.payload
		},

		// get access control
		USER_ACCESS_CONTROL_STATUS: (
			state: AccessControlStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.userAccessControlStatus = action.payload
		},
		GROUP_ACCESS_CONTROL_STATUS: (
			state: AccessControlStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.groupAccessControlStatus = action.payload
		},
		SELECTED_ACCESS_USER: (
			state: AccessControlStateType,
			action: PayloadAction<UserType>,
		) => {
			state.selectedAccessUser = action.payload
		},
		SELECTED_ACCESS_GROUP: (
			state: AccessControlStateType,
			action: PayloadAction<AccessGroupType>,
		) => {
			state.selectedAccessGroup = action.payload
		},
		SELECTED_ACCESS_GROUP_BY_USER_TAB: (
			state: AccessControlStateType,
			action: PayloadAction<AccessGroupType>,
		) => {
			state.selectedAccessGroupByUserTab = action.payload
		},
		GROUP_LIST_BY_USER_TAB: (
			state: AccessControlStateType,
			action: PayloadAction<AccessGroupType[]>,
		) => {
			state.groupListByUserTab = action.payload
		},

		// access control
		SELECTED_GROUP_ACCESS_CONTROL: (
			state: AccessControlStateType,
			action: PayloadAction<AccessControlType[]>,
		) => {
			state.selectedGroupAccessControl = action.payload
		},
		SELECTED_GROUP_ACCESS_CONTROL_BY_USER_TAB: (
			state: AccessControlStateType,
			action: PayloadAction<AccessControlType[]>,
		) => {
			state.selectedGroupAccessControlByUserTab = action.payload
		},
		OPENED_USER_ACCESS_CONTROL: (
			state: AccessControlStateType,
			action: PayloadAction<string[]>,
		) => {
			state.openedUserAccessControl = action.payload
		},

		// access control action
		ADD_ACCESS_CONTROL: (
			state: AccessControlStateType,
			action: PayloadAction<AccessControlStateType['addAccessControl']>,
		) => {
			state.addAccessControl = action.payload
		},
		UPDATE_ACCESS_CONTROL: (
			state: AccessControlStateType,
			action: PayloadAction<AccessControlStateType['updateAccessControl']>,
		) => {
			state.updateAccessControl = action.payload
		},
		DELETE_ACCESS_CONTROL: (
			state: AccessControlStateType,
			action: PayloadAction<string>,
		) => {
			state.deleteAccessControl = action.payload
		},

		// left modal
		SHOW_SIDE_MODAL: (state: AccessControlStateType, action: PayloadAction<boolean>) => {
			state.showSideModal = action.payload
		},
		LEFT_MODAL_INFO: (
			state: AccessControlStateType,
			action: PayloadAction<AccessControlStateType['leftModalInfo']>,
		) => {
			state.leftModalInfo = action.payload
		},
		LEFT_MODAL_TAB_INDEX: (
			state: AccessControlStateType,
			action: PayloadAction<number>,
		) => {
			state.leftModalTabIndex = action.payload
		},
	},
})

export const accessControlActions = slice.actions
export default slice.reducer
